import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Bot, RefreshCw, Search, RotateCcw, AlertCircle } from 'lucide-react';
import { notify } from '@core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';

type Persona =
  | 'system_admin'
  | 'partner_admin'
  | 'company_admin'
  | 'compliance_user'
  | 'internal_auditor'
  | 'external_auditor';

interface CapabilityRow {
  task_name: string;
  description: string;
  risk_level: 'low' | 'medium' | 'high';
  writes_data: boolean;
  requires_confirmation: boolean;
  required_permissions: string[];
  global_enabled: boolean;
  role_enabled: Record<Persona, boolean>;
  has_override: boolean;
}

const PERSONA_LABELS: Record<Persona, string> = {
  system_admin: 'System Admin',
  partner_admin: 'Partner Admin',
  company_admin: 'Company Admin',
  compliance_user: 'Compliance User',
  internal_auditor: 'Internal Auditor',
  external_auditor: 'External Auditor'
};

const RISK_STYLES: Record<'low' | 'medium' | 'high', string> = {
  low: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800'
};

const getToken = () => localStorage.getItem('token') || '';
const getCsrfToken = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

const AICapabilityManager: React.FC = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingTask, setSavingTask] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilityRow[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [search, setSearch] = useState('');

  const canManage = hasPermission('system.config');

  const loadMatrix = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/ai/capabilities/manager', {
        headers: {
          Authorization: `Bearer ${getToken()}`
        }
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to load capability matrix');
      }

      setPersonas((payload.data?.personas || []) as Persona[]);
      setCapabilities((payload.data?.capabilities || []) as CapabilityRow[]);
    } catch (error: any) {
      notify.error(error?.message || 'Failed to load AI capability manager');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    loadMatrix();
  }, [canManage]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return capabilities;
    return capabilities.filter((cap) => (
      cap.task_name.toLowerCase().includes(query) ||
      cap.description.toLowerCase().includes(query) ||
      cap.required_permissions.some((perm) => perm.toLowerCase().includes(query))
    ));
  }, [capabilities, search]);

  const capabilitySummary = useMemo(() => {
    const total = capabilities.length;
    const globalEnabled = capabilities.filter(cap => cap.global_enabled).length;
    const byPersona = personas.map(persona => ({
      persona,
      enabled: capabilities.filter(cap => Boolean(cap.role_enabled?.[persona])).length
    }));

    return { total, globalEnabled, byPersona };
  }, [capabilities, personas]);

  const saveCapability = async (taskName: string, body: Record<string, unknown>) => {
    try {
      setSavingTask(taskName);
      const csrf = getCsrfToken();
      const res = await fetch(`/api/ai/capabilities/manager/${encodeURIComponent(taskName)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify(body)
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to update capability');
      }

      if (payload.data) {
        setCapabilities(prev => prev.map(item => item.task_name === taskName ? payload.data : item));
      } else {
        await loadMatrix();
      }
    } catch (error: any) {
      notify.error(error?.message || `Failed to update ${taskName}`);
      await loadMatrix();
    } finally {
      setSavingTask(null);
    }
  };

  const resetCapability = async (taskName: string) => {
    try {
      setSavingTask(taskName);
      const csrf = getCsrfToken();
      const res = await fetch(`/api/ai/capabilities/manager/${encodeURIComponent(taskName)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        }
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to reset capability');
      }

      if (payload.data) {
        setCapabilities(prev => prev.map(item => item.task_name === taskName ? payload.data : item));
      } else {
        await loadMatrix();
      }
      notify.success(`Reset ${taskName} to defaults`);
    } catch (error: any) {
      notify.error(error?.message || `Failed to reset ${taskName}`);
    } finally {
      setSavingTask(null);
    }
  };

  if (!canManage) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8">
          <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
          <div className="flex items-center gap-1">
            <h1 className="text-2xl font-bold text-slate-900">Access Denied</h1>
            <PageInfoButton />
          </div>
          <p className="text-slate-600 mt-2">You need `system.config` permission to manage AI capabilities.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Bot className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">AI Capability Manager</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-600 mt-1">
              Toggle capabilities globally and by role without redeploying code.
            </p>
          </div>
          <button
            onClick={loadMatrix}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search capability, description, or permission..."
            className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Total Capabilities</div>
            <div className="text-xl font-semibold text-slate-900">{capabilitySummary.total}</div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Globally Enabled</div>
            <div className="text-xl font-semibold text-slate-900">{capabilitySummary.globalEnabled}</div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Globally Disabled</div>
            <div className="text-xl font-semibold text-slate-900">
              {Math.max(0, capabilitySummary.total - capabilitySummary.globalEnabled)}
            </div>
          </div>
        </div>

        {capabilitySummary.byPersona.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {capabilitySummary.byPersona.map(item => (
              <div key={item.persona} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-700">
                <span className="font-medium">{PERSONA_LABELS[item.persona]}</span>
                <span>{item.enabled}/{capabilitySummary.total}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1500px] w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Capability</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Risk</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Confirm</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Global</th>
                {personas.map((persona) => (
                  <th key={persona} className="text-left px-4 py-3 font-semibold text-slate-700 whitespace-nowrap">
                    {PERSONA_LABELS[persona]}
                  </th>
                ))}
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6 + personas.length} className="px-4 py-8 text-center text-slate-500">
                    Loading capability matrix...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6 + personas.length} className="px-4 py-8 text-center text-slate-500">
                    No capabilities found.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((cap) => {
                const rowBusy = savingTask === cap.task_name;
                return (
                  <tr key={cap.task_name} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-slate-900">{cap.task_name}</div>
                      <div className="text-xs text-slate-600 mt-1 max-w-[420px]">{cap.description}</div>
                      {cap.required_permissions.length > 0 && (
                        <div className="text-xs text-slate-500 mt-1">
                          Requires: {cap.required_permissions.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex rounded px-2 py-1 text-xs font-medium ${RISK_STYLES[cap.risk_level]}`}>
                        {cap.risk_level.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-700">{cap.writes_data ? 'Write' : 'Read'}</td>
                    <td className="px-4 py-3 align-top text-slate-700">{cap.requires_confirmation ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={cap.global_enabled}
                        disabled={rowBusy}
                        onChange={(e) => saveCapability(cap.task_name, { global_enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    {personas.map((persona) => (
                      <td key={`${cap.task_name}-${persona}`} className="px-4 py-3 align-top">
                        <input
                          type="checkbox"
                          checked={Boolean(cap.role_enabled?.[persona])}
                          disabled={rowBusy}
                          onChange={(e) => {
                            const nextRoles = {
                              ...cap.role_enabled,
                              [persona]: e.target.checked
                            };
                            saveCapability(cap.task_name, { role_enabled: nextRoles });
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3 align-top">
                      <button
                        onClick={() => resetCapability(cap.task_name)}
                        disabled={rowBusy || !cap.has_override}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                        title="Reset to default role map"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AICapabilityManager;
